import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { backendClient } from '@/lib/api-client';

export interface SyncedCalendar {
  calendar_id: string;
  provider: string;
  synced_at: string | null;
}

export interface OutlookSyncStatus {
  event_guid: string;
  event_id: number;
  synced_calendars: SyncedCalendar[];
  is_synced: boolean;
}

export interface BulkCalendarSyncResult {
  synced: number;
  failed: number;
}

export const useCalendarSync = (matterGuid: string) => {
  const inFlightRef = useRef(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const withSyncing = async <T,>(run: () => Promise<T>): Promise<T> => {
    inFlightRef.current += 1;
    setIsSyncing(true);
    try {
      return await run();
    } finally {
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      setIsSyncing(inFlightRef.current > 0);
    }
  };

  const syncToOutlook = async (
    taskGuid: string,
    calendarId?: string,
    options?: { silent?: boolean },
  ): Promise<boolean> => {
    const run = async () => {
      const requestBody = {
        calendar_id: calendarId || 'primary',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      try {
        await backendClient.post(`/deadlines/${matterGuid}/${taskGuid}/sync-to-outlook`, requestBody);
        if (!options?.silent) toast.success('Synced to Outlook calendar');
        return true;
      } catch (error: any) {
        console.error('Error syncing to Outlook:', error);
        toast.error(error.response?.data?.detail || 'Failed to sync to Outlook');
        return false;
      }
    };
    return options?.silent ? run() : withSyncing(run);
  };

  const unsyncFromOutlook = async (
    taskGuid: string,
    calendarId: string,
    myCalendarId?: string,
    options?: { silent?: boolean },
  ): Promise<boolean> => {
    const run = async () => {
      try {
        let url = `/deadlines/${matterGuid}/${taskGuid}/sync-to-outlook?calendar_id=${encodeURIComponent(calendarId)}`;
        if (myCalendarId) url += `&my_calendar_id=${encodeURIComponent(myCalendarId)}`;
        await backendClient.delete(url);
        if (!options?.silent) toast.success('Removed from Outlook calendar');
        return true;
      } catch (error: any) {
        console.error('Error removing Outlook sync:', error);
        toast.error(error.response?.data?.detail || 'Failed to remove sync');
        return false;
      }
    };
    return options?.silent ? run() : withSyncing(run);
  };

  const syncDeadlinesToOutlook = async (
    taskGuids: string[],
    calendarId: string,
  ): Promise<BulkCalendarSyncResult> => {
    if (taskGuids.length === 0) return { synced: 0, failed: 0 };

    return withSyncing(async () => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let synced = 0;
      const errors: unknown[] = [];

      // Keep Graph traffic bounded for matters with many deadlines.
      for (let index = 0; index < taskGuids.length; index += 5) {
        const batch = taskGuids.slice(index, index + 5);
        const results = await Promise.allSettled(
          batch.map((taskGuid) =>
            backendClient.post(`/deadlines/${matterGuid}/${taskGuid}/sync-to-outlook`, {
              calendar_id: calendarId,
              timezone,
            }),
          ),
        );

        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            synced += 1;
          } else {
            errors.push(result.reason);
          }
        });
      }

      if (synced > 0) {
        toast.success(`Synced ${synced} deadline${synced === 1 ? '' : 's'} to Outlook`);
      }
      if (errors.length > 0) {
        const firstError = errors[0] as { response?: { data?: { detail?: string } } };
        const detail = firstError.response?.data?.detail;
        toast.error(
          detail ||
            `Failed to sync ${errors.length} deadline${errors.length === 1 ? '' : 's'} to Outlook`,
        );
      }

      return { synced, failed: errors.length };
    });
  };

  const getSyncStatus = async (taskGuid: string): Promise<OutlookSyncStatus | null> => {
    try {
      const response = await backendClient.get(`/deadlines/${matterGuid}/${taskGuid}/sync-status`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching sync status:', error);
      return null;
    }
  };

  return {
    syncToOutlook,
    syncDeadlinesToOutlook,
    unsyncFromOutlook,
    getSyncStatus,
    isSyncing,
  };
};
